import React, { MouseEvent, useCallback, useMemo, useState } from 'react';

import capitalize from 'lodash/capitalize';
import sortBy from 'lodash/sortBy';

import { ContentCopyOutlined } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridCellModes,
  GridCellModesModel,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import GridPagination from '~/components/Common/Elements/GridPagination';

import {
  BEDBANK_COMMENTS_ENUM,
  BEDBANK_PROPERTY_DOMAINS_ENUM,
  BEDBANK_PROPERTY_DOMAIN_LE,
  BEDBANK_PROPERTY_TYPES_ENUM,
  SUPPLIER_NAMES,
} from '~/consts/bedbank';

import { useCurateProperties } from '~/hooks/apis/bedbank/useCuration';

import BedbankRowExpansionContent from './BedbankRowExpansionContent';
import BulkActionsButton from './BulkActionsButton';
import CheckboxGroup from './CheckboxGroup';
import DomainCell from './DomainCell';
import PreviewCell from './PreviewCell';

interface Property {
  id: string;
  name: string;
  rating: number;
}

interface Props {
  count: number;
  data: Array<Property>;
  handleSelect: (selectedIds: GridRowSelectionModel) => void;
  onOpenModalBulkUpdate: () => void;
  onOpenModalImage: (id: string) => void;
  onPageChange: (page: number, sizePerPage: number) => void;
  onStatusUpdate: (newRow) => any;
  page: number;
  selected: GridRowSelectionModel;
  sizePerPage: number;
  updateImageData: (images: Array<any>, rowId: string) => void;
}

const urlBuilder = (bedbankPropertyId: string) => {
  return `/bedbank/properties/${bedbankPropertyId}`;
};

export default function BedbankPropertiesTable({
  count,
  data,
  handleSelect,
  onOpenModalBulkUpdate,
  onOpenModalImage,
  onPageChange,
  onStatusUpdate,
  page,
  selected,
  sizePerPage,
  updateImageData,
}: Props) {
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const apiRef = useGridApiRef();
  const curateProperties = useCurateProperties();

  // src: https://mui.com/x/react-data-grid/recipes-editing/#single-click-editing
  const handleCellClick = useCallback((params: GridCellParams, event: MouseEvent) => {
    if (!params.isEditable) {
      return;
    }

    // Ignore portal
    if ((event.target as any).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
      return;
    }

    setCellModesModel((prevModel) => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
          }),
          {},
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {},
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const columns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Property',
        disableColumnMenu: true,
        width: 240,
        minWidth: 200,
        display: 'flex',
        renderCell: ({ row, value }) => (
          <Stack spacing={0.5} alignItems="flex-start" py={1}>
            <Link href={urlBuilder(row.id)} target="_blank" rel="noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
              {value}
            </Link>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Tooltip title={row.id}>
                <Typography color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {row.id.slice(0, 8)}
                </Typography>
              </Tooltip>
              <IconButton
                size="small"
                aria-label="Copy property ID"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(row.id);
                }}
              >
                <ContentCopyOutlined fontSize="small" />
              </IconButton>
            </Stack>
            {row.rating > 0 && (
              <Typography variant="body2" color="text.secondary">
                {row.rating} stars
              </Typography>
            )}
            <Button size="small" sx={{ px: 0, minWidth: 0 }} onClick={() => apiRef.current.toggleDetailPanel(row.id)}>
              Photos
            </Button>
          </Stack>
        ),
      },
      {
        field: 'masterSupplier',
        headerName: 'Suppliers',
        disableColumnMenu: true,
        width: 150,
        minWidth: 150,
        renderCell: ({ row }: GridRenderCellParams) => {
          const suppliers = sortBy(SUPPLIER_NAMES, (s) => (s === row.masterSupplier ? 0 : 1)).filter(
            (supplier) => row[`${supplier}Id`],
          );
          return (
            <Stack direction="row" flexWrap="wrap" gap={0.5} py={0.5}>
              {suppliers.map((supplier) => {
                const supplierId = row[`${supplier}Id`];
                const isMaster = supplier === row.masterSupplier;
                return (
                  <Tooltip key={supplier} title={`${supplierId} (click to copy)`}>
                    <Chip
                      size="small"
                      label={capitalize(supplier)}
                      variant={isMaster ? 'filled' : 'outlined'}
                      color={isMaster ? 'primary' : 'default'}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(supplierId);
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          );
        },
      },
      {
        field: 'country',
        headerName: 'Address',
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams) => (
          <Link href={urlBuilder(row.id)} target="_blank" rel="noreferrer" underline="hover" color="inherit">
            {row.country ? <b>{row.country},</b> : '-'}
            <br />
            {row.stateProvinceName ? `${row.stateProvinceName},` : '-'}
            <br />
            {row.city ?? '-'}
          </Link>
        ),
        width: 150,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'singleSelect',
        valueOptions: BEDBANK_PROPERTY_TYPES_ENUM,
        editable: true,
        disableColumnMenu: true,
        renderCell: ({ value }: GridRenderCellParams) => <Link>{value}</Link>,
        width: 110,
        display: 'flex',
      },
      {
        field: 'profile',
        headerName: 'Profile',
        disableColumnMenu: true,
        width: 80,
        display: 'flex',
      },
      {
        field: 'chainName',
        headerName: 'Chain Name',
        disableColumnMenu: true,
        width: 130,
        display: 'flex',
      },
      {
        field: 'status',
        headerName: 'Status',
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row, value }: GridRenderCellParams) => (
          <Stack>
            <CheckboxGroup
              id={row.id}
              value={value}
              onStatusUpdate={(value) => onStatusUpdate({ ...row, status: value })}
            />
          </Stack>
        ),
        width: 160,
        minWidth: 160,
      },
      {
        field: 'domain',
        headerName: 'Domain',
        editable: true,
        type: 'singleSelect',
        valueOptions: BEDBANK_PROPERTY_DOMAINS_ENUM,
        getOptionDisabled: (value) => value === BEDBANK_PROPERTY_DOMAIN_LE,
        disableColumnMenu: true,
        align: 'center',
        renderCell: DomainCell,
        width: 110,
        display: 'flex',
      },
      {
        field: 'isPreferred',
        headerName: 'Is Preferred',
        type: 'boolean',
        editable: true,
        disableColumnMenu: true,
        renderCell: ({ value }: GridRenderCellParams) => <Link>{value ? 'Yes' : 'No'}</Link>,
        width: 110,
        display: 'flex',
      },
      {
        field: 'preview',
        headerName: 'Preview',
        disableColumnMenu: true,
        renderCell: PreviewCell,
        width: 110,
        display: 'flex',
      },
      {
        field: 'comment',
        headerName: 'Comment',
        disableColumnMenu: true,
        type: 'singleSelect',
        valueOptions: BEDBANK_COMMENTS_ENUM,
        editable: true,
        renderCell: ({ value }: GridRenderCellParams) => (value ? <Link>{value}</Link> : '-'),
        valueFormatter: (value) => capitalize(value),
        width: 160,
        display: 'flex',
      },
    ],
    [apiRef, onStatusUpdate],
  );

  return (
    <Box mt={3} sx={{ minWidth: 0, width: '100%' }}>
      <BulkActionsButton
        actions={[
          { label: 'Update Properties', onClick: onOpenModalBulkUpdate },
          {
            label: 'Curate Selected',
            disabled: curateProperties.isPending,
            onClick: () => {
              curateProperties.mutate(selected as Array<string>);
            },
          },
        ]}
        disabled={!selected || selected.length === 0}
      />
      <DataGridPro
        apiRef={apiRef}
        sx={{ height: '70vh', minHeight: 400, mt: 1 }}
        columnVisibilityModel={{ [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false }}
        cellModesModel={cellModesModel}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        getDetailPanelHeight={() => 'auto'}
        getDetailPanelContent={({ row }) => (
          <BedbankRowExpansionContent row={row} updateImageData={updateImageData} onOpenModalImage={onOpenModalImage} />
        )}
        getRowHeight={() => 'auto'}
        onRowSelectionModelChange={handleSelect}
        rowSelectionModel={selected}
        rows={data ?? []}
        pagination
        onCellModesModelChange={(newModel: GridCellModesModel) => setCellModesModel(newModel)}
        onCellClick={handleCellClick}
        processRowUpdate={(newRow, _oldRow) => onStatusUpdate(newRow)}
        onPaginationModelChange={({ page }) => onPageChange(page + 1, sizePerPage)}
        paginationModel={{ page: page - 1, pageSize: sizePerPage }}
        paginationMode="server"
        rowCount={count}
        density="compact"
        slots={{ pagination: GridPagination }}
      />
    </Box>
  );
}
